const SKIP_INVENTORY_STOCK_CHECK = false;
const STOREFRONT_ACCESS_TOKEN = 'cf1d4540fe597eef6bf5332e8217e873';
const STOREFRONT_API_VERSION = '2025-07';
const INVENTORY_LOCATION_NAME = 'Seko';
const VARIANT_INVENTORY_QUERY = `
  query VariantInventoryAtLocation($variantId: ID!) {
    node(id: $variantId) {
      ... on ProductVariant {
        id
        quantityAvailable
        storeAvailability(first: 20) {
          nodes {
            quantityAvailable
            available
            location {
              name
            }
          }
        }
      }
    }
  }
`;

// Shopify only creates an option on a bundle product for components the buyer can choose, so a
// component locked to a single variant is absent from the Liquid product object entirely. The
// Storefront API is the only storefront-side source that still knows about it.
const BUNDLE_COMPONENTS_QUERY = `
  query VariantBundleComponents($variantId: ID!) {
    node(id: $variantId) {
      ... on ProductVariant {
        id
        components(first: 20) {
          nodes {
            quantity
            productVariant {
              id
              title
              image {
                url(transform: { maxWidth: 200, maxHeight: 200 })
              }
              product {
                id
                title
                handle
                featuredImage {
                  url(transform: { maxWidth: 200, maxHeight: 200 })
                }
              }
            }
          }
        }
      }
    }
  }
`;

class WCProductForm extends HTMLElement {
  constructor() {
    super();
    Object.assign(this, {
      form: null,
      variantIndex: null,
      productOptionSelectors: null,
      productOptionCategories: null,
      variantInput: null,
      productJSON: null,
      variants: null,
      activeVariant: null,
      variantsData: null,
      validationInProgress: false,
      options: null,
      backgrounds: null,
      quantity: null,
      optionSelected: null,
      stickyOptionSelected: null,
      stickySelected: null,
      wcSticky: null,
      buttonContainer: null,
      lowInventoryMessage: null,
      lowInventoryConfig: null,
      inventoryCache: {},
      lowInventoryRequestId: 0,
      variantChangeInProgress: false,
      bundleOptionColorMaps: {},
      bundleOptionMaps: {},
      isBundle: false,
    });
  }

  get formActiveVartiant() {
    return this.activeVariant
  }

  connectedCallback() {
    this.productJSON = JSON.parse(this.dataset.productJson);
    document.addEventListener('DOMContentLoaded', this.trackViewItem);

    this.wcSticky = document.querySelector('wc-product-sticky-bar');
    this.wcStickyForm = this.wcSticky?.querySelector('form');
    this.stickyOptionSelectedList = this.wcSticky?.querySelectorAll('[data-selected-title]');
    this.stickySelectedList = this.wcSticky?.querySelectorAll('[data-sticky-selected-swatch]');
    this.stickyProductStickyOptionSelectors = this.wcStickyForm?.querySelectorAll(
      '[data-target="options"]'
    );
    this.stickyProductStickyOptionSelectors?.forEach((selector) => {
      selector.addEventListener('change', () => {
        this.updateVariantInput(selector);
      });
    });
    this.wcStickyForm?.addEventListener(
      'submit',
      this.handleAddToBag.bind(this)
    );

    this.productOptionSelectors = this.querySelectorAll(
      '[data-target="options"]'
    );
    this.faqAnchor = this.querySelector('[href="#faq"]');
    this.optionSelectedList = this.querySelectorAll('.wc-product-options__color-name');
    this.buttonContainer = this.querySelector('.wc-product-button-container');
    this.lowInventoryMessage = this.querySelector('[data-low-inventory-message]');
    this.lowInventoryConfig = this.parseLowInventoryConfig();
    this.productOptionCategories = this.querySelectorAll('[data-category]');
    this.form = this.querySelector('.js-main-pdp-form');
    this.productOptionLabels = this.querySelectorAll('[data-option-category]');
    this.variantInput = this.form?.querySelector('[data-target="variant-id"]');
    this.variantIndex =
      this.form?.querySelector('[data-variant-index]:checked')?.dataset
        ?.variantIndex ?? 0;
    this.variants = this.productJSON.variants;
    this.options = this.productJSON.options;
    this.variantsData = this.productJSON.variants_display_data;
    this.backgrounds = this.productJSON.backgrounds;
    this.quantity = this.querySelector('[data-quantity-input]');
    this.isBundle = Boolean(this.productJSON.is_bundle) || this.classList.contains('wc-product-form--bundle');
    // custom.disable_variant_button on the bundle product. Every component reaching
    // buildFixedComponentCard is locked to one variant by definition, so this drops the option
    // row on all of them - the Liquid path has to check option.values.size itself because its
    // options can still be real choices.
    this.disableVariantButton = Boolean(this.productJSON.disable_variant_button);
    this.optionValueImages = this.buildOptionValueImages();
    this.bundleOptionMaps = this.buildBundleOptionMaps();
    this.bundleOptionColorMaps = this.buildBundleOptionColorMaps();
    this.initBundleOptions();
    this.initFixedBundleComponents();

    if (this.querySelector('.wc-add-to-cart__buy-now')) {
      this.querySelector('.wc-add-to-cart__buy-now').addEventListener(
        'click',
        (e) => this.buyItNowHandle(e)
      );
    }

    document.addEventListener('variant:change', (e) => {
      this.buttonContainer?.classList.add('active');
      this.validate(e.detail.activeVariant);
      this.variantIndex = e.detail.activeVariantIndex;
    });

    this.form?.addEventListener('submit', this.handleAddToBag.bind(this));

    this.productOptionSelectors?.forEach((selector) => {
      selector.addEventListener('change', () => {
        this.updateVariantInput(selector);
      });
    });

    if (this.faqAnchor) {
      this.faqAnchor.addEventListener('click', (e) => {
        e.preventDefault();
        const faqSection = document.querySelector(
          this.faqAnchor.getAttribute('href')
        );
        if (faqSection) {
          const faqToggleMobile = document.querySelector('.js-wc-tab-faq-mobile');
          faqToggleMobile?.setAttribute('open', '');
          const faqToggleDesktop = document.querySelector('.js-wc-tab-faq-desktop');
          faqToggleDesktop?.dispatchEvent(new Event('click'));

          faqSection.scrollIntoView();
        }
      });
    }

    this.productOptionCategories?.forEach((selector) => {
      selector.addEventListener('click', () => {
        const currentActive = this.querySelector('.active[data-category]');
        currentActive.classList.remove('active');
        selector.classList.add('active');
        this.filterColor(selector.dataset.category);
      });
    });
    const formRendered = new CustomEvent('form:rendered');
    document.dispatchEvent(formRendered);

    this.querySelectorAll('.js-wc-product__accordion')
      .forEach((accGroup) => {
        const trigger = accGroup.querySelector('.js-wc-product__button')
        const content = accGroup.querySelector('.js-wc-product__content')
        if (!trigger || !content) { return }
        trigger.addEventListener('click', () => {
          accGroup.classList.toggle('open')
        })
      })

    const ingredientsWrapper = this.querySelector('.js-wc-product__ingredients')
    if (ingredientsWrapper !== null) {
      const ingredientsToggles = ingredientsWrapper.querySelectorAll('.js-wc-product__ingredients-toggle')
      ingredientsToggles.forEach(toggle => {
        toggle.addEventListener('click', (event) => {
          event.preventDefault()
          ingredientsWrapper?.classList.toggle('active');
          this.querySelector
        })
      })
    }

    this.validate();
    this.stockCheck();
    this.updateSwatchColors();
    this.updateOptionSelectedLabels();
  }

  parseLowInventoryConfig = () => {
    const configNode = this.querySelector('[data-variant-low-inventory-config]');

    if (!configNode) {
      return {
        shopDomain: this.dataset.shopDomain,
        inventoryLocation: INVENTORY_LOCATION_NAME,
        variants: {},
      };
    }

    try {
      return JSON.parse(configNode.textContent);
    } catch (error) {
      console.error('Unable to parse low inventory config:', error);
      return null;
    }
  };

  getLowInventoryVariantConfig = (variantId) => {
    if (!this.lowInventoryConfig?.variants) return null;

    return (
      this.lowInventoryConfig.variants[String(variantId)] ??
      this.lowInventoryConfig.variants[variantId] ??
      null
    );
  };

  fetchSekoInventory = async (variantId) => {
    const cachedQuantity = this.inventoryCache[variantId];

    if (cachedQuantity !== undefined) {
      return cachedQuantity;
    }

    const storefrontAccessToken =
      this.lowInventoryConfig?.storefrontAccessToken || STOREFRONT_ACCESS_TOKEN;

    if (!storefrontAccessToken) {
      return null;
    }

    const response = await fetch(
      `/api/${STOREFRONT_API_VERSION}/graphql.json`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': storefrontAccessToken,
        },
        body: JSON.stringify({
          query: VARIANT_INVENTORY_QUERY,
          variables: {
            variantId: `gid://shopify/ProductVariant/${variantId}`,
          },
        }),
      }
    );

    if (!response.ok) {
      throw new Error(`Inventory request failed with status ${response.status}`);
    }

    const payload = await response.json();
    if (payload.errors?.length) {
      throw new Error(payload.errors.map((error) => error.message).join(', '));
    }

    const variant = payload.data?.node;
    if (!variant) {
      return null;
    }

    const locationName = this.lowInventoryConfig?.inventoryLocation || INVENTORY_LOCATION_NAME;
    const locationAvailability = variant.storeAvailability?.nodes?.find(
      (node) => node.location?.name === locationName
    );

    const quantity =
      locationAvailability?.quantityAvailable ??
      variant.quantityAvailable ??
      0;

    this.inventoryCache[variantId] = quantity;
    return quantity;
  };

  hideLowInventoryMessage = () => {
    if (!this.lowInventoryMessage) return;

    this.lowInventoryMessage.textContent = '';
    this.lowInventoryMessage.classList.add('hidden');
  };

  showLowInventoryMessage = (message) => {
    if (!this.lowInventoryMessage || !message) return;

    this.lowInventoryMessage.textContent = message;
    this.lowInventoryMessage.classList.remove('hidden');
  };

  checkLowInventoryMessage = async (activeVariant) => {
    if (!this.lowInventoryMessage || !this.lowInventoryConfig) return;

    activeVariant = activeVariant ?? this.activeVariant ?? this.getActiveVariant();
    if (!activeVariant?.id) {
      this.hideLowInventoryMessage();
      return;
    }

    const variantConfig = this.getLowInventoryVariantConfig(activeVariant.id);
    const message = variantConfig?.low_inventory_message;

    if (SKIP_INVENTORY_STOCK_CHECK) {
      if (message) {
        this.showLowInventoryMessage(message);
      } else {
        this.hideLowInventoryMessage();
      }
      return;
    }

    const stockThreshold = variantConfig?.low_inventory_stock_level;

    if (stockThreshold === null || stockThreshold === undefined) {
      this.hideLowInventoryMessage();
      return;
    }


    const requestId = ++this.lowInventoryRequestId;


    try {
      const availableQuantity = await this.fetchSekoInventory(activeVariant.id);

      if (requestId !== this.lowInventoryRequestId) return;

      if (availableQuantity === null) {
        this.hideLowInventoryMessage();
        return;
      }

      if (availableQuantity < stockThreshold) {
        this.showLowInventoryMessage(variantConfig.low_inventory_message);
      } else {
        this.hideLowInventoryMessage();
      }
    } catch (error) {
      if (requestId !== this.lowInventoryRequestId) return;

      console.error('Low inventory check failed:', error);
      this.hideLowInventoryMessage();
    }
  };

  trackViewItem = () => {
    window.trackEvents.viewItem({ product: this.productJSON });
  }

  stockCheck = (activeVariant) => {
    if (!activeVariant)
      activeVariant = this.activeVariant
        ? this.activeVariant
        : this.getActiveVariant();

    if (!activeVariant.available || activeVariant.isOos) {
      this.classList.add('pdp-oos')

      this.buttonContainer?.classList.add('oos-state');
      if (this.wcSticky) {
        this.wcSticky
          .querySelector('#add-to-cart-button-sticky')
          .classList.add('oos-state');

        const stickyAtcText = activeVariant.has_sold_out_tag
          ? this.productJSON.language.soldOut
          : this.productJSON.language.notify

        this.wcSticky.querySelector(
          '#add-to-cart-button-sticky span'
        ).textContent = stickyAtcText
      }

    } else {
      this.classList.remove('pdp-oos')

      if (this.quantity) {
        const variantQuantity = this.variantsData?.find(
          (data) => data.id === activeVariant.id
        )?.inventory_quantity ?? 100

        this.quantity.max = variantQuantity
      }

      this.buttonContainer?.classList.remove('oos-state');
      this.querySelectorAll('[data-add-to-cart1] span')?.forEach((button) => {
        button.textContent = this.productJSON.language.addToCart;
      });
      if (this.wcSticky) {
        this.wcSticky
          .querySelector('#add-to-cart-button-sticky')
          .classList.remove('oos-state');
        this.wcSticky.querySelector(
          '#add-to-cart-button-sticky span'
        ).textContent = this.productJSON.language.addToCart;
      }
    }
  };

  formatPrice = (cents, useDecimalPricing) => {
    if (!useDecimalPricing) {
      return `$${(cents / 100).toFixed(0)}`;
    }
    return `$${(cents / 100).toFixed(2)}`;
  };

  hasDecimalPricing = () => {
    const tag = this.productJSON.decimal_pricing_tag;
    return Boolean(tag && this.productJSON.tags?.includes(tag));
  };

  validate = (activeVariant) => {
    if (this.validationInProgress) return;
    this.validationInProgress = true;
    activeVariant = activeVariant ? activeVariant : this.getActiveVariant();
    this.activeVariant = activeVariant;
    const hasVariant = activeVariant !== null;
    const price = document.querySelectorAll('[data-target="product-price"]');
    const useDecimalPricing = this.hasDecimalPricing();
    let formattedVariantPrice;
    let comparePrice;
    let priceHtml;

    this.variantInput.value = activeVariant?.id;

    if (hasVariant) {
      formattedVariantPrice = this.formatPrice(activeVariant.price, useDecimalPricing);
      comparePrice = this.formatPrice(activeVariant.compare_at_price, useDecimalPricing);

      let comparePriceHtml = '';
      if (activeVariant.compare_at_price > activeVariant.price) {
        if (this.isBundle) {
          const valuedAtLabel = this.productJSON.language?.valuedAt || 'Valued at';
          comparePriceHtml = `<span class="wc-product-price__valued" data-target="compare-price">${valuedAtLabel} ${comparePrice}</span>`;
        } else {
          comparePriceHtml = `<del data-target="compare-price">${comparePrice}</del>`;
        }
      }

      priceHtml = `
            <p>
                <span id="pricePlacement">${formattedVariantPrice}</span>
                ${comparePriceHtml}
            </p>`;

      if (window.location.pathname.includes('/products/')) {
        this.updateURL(activeVariant.id);
      }
    }

    price.forEach((item) => {
      const el = item;
      el.innerHTML = priceHtml;
    });

    this.stockCheck(activeVariant);
    this.checkLowInventoryMessage(activeVariant);
    this.validationInProgress = false;
  };

  handleAddToBag = (event) => {
    event.preventDefault();
    event.stopPropagation();

    const activeVariant = this.getActiveVariant();
    const canAddToCart = activeVariant.available && !activeVariant.isOos;

    if (!canAddToCart) {
      if (activeVariant.isComing) {
        this.buttonContainer?.classList.remove('active');
      } else if (activeVariant.isOos) {
        this.buttonContainer?.classList.remove('active');
      } else {
        this.buttonContainer?.classList.remove('active');
      }

      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth',
      });

      return;
    }

    const formD = new FormData(this.form);
    const sellingPlanId = formD.get('selling_plan');
    const originalProductQuantity = formD.get('quantity');

    let formData = {
      items: [
        {
          id: Number(activeVariant.id),
          quantity: originalProductQuantity,
        },
      ],
      sections: "wc-mini-cart"
    };

    if (sellingPlanId) {
      formData.items[0].selling_plan = sellingPlanId;
    }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formData),
    })
      .then(async (response) => {
        const payload = await response.json();

        // /cart/add.js answers a rejection with {status, message, description} and no items.
        // Reading data.items off that turns every cart error into an opaque TypeError, which
        // hides the one thing worth knowing - why Shopify refused the line.
        if (!response.ok) {
          throw new Error(
            payload.description ||
              payload.message ||
              `Add to cart failed with status ${response.status}`
          );
        }

        return payload;
      })
      .then((data) => {
        window.trackEvents.addToCart({
          lineItems: (data.items || []).map((item) => ({
            ...item,
            quantity: originalProductQuantity,
          })),
        });
        window.dispatchEvent(new CustomEvent('MINI_CART_ACTION', {
          detail: {
            action: 'OPEN',
            cartResponseJson: data
          }
        }))
        return false
      })
      .catch((error) => {
        console.error('Add to cart failed:', error.message || error);
      });
  };

  filterColor = (category) => {
    this.productOptionLabels.forEach((optionLabel) => {
      if (category === 'all') {
        optionLabel.classList.remove('hidden');
      } else {
        if (optionLabel.dataset.optionCategory === category) {
          optionLabel.classList.remove('hidden');
        } else {
          optionLabel.classList.add('hidden');
        }
      }
    });
    const firstCategorySelector = Array.from(this.productOptionLabels)
      .find((node) => node.dataset.optionCategory === category)
      .querySelector('input');
    this.updateVariantInput(firstCategorySelector);
  };

  updateURL = (variantId) => {
    if (!variantId || this.dataset.updateUrl === 'false') return;
    const url = new URL(location.href);
    url.searchParams.set('variant', variantId)
    window.history.replaceState({}, '', url);
  };

  getVariantForOptionValue = (activeVariant, optionName, optionValue) => {
    const targetOptions = {
      option1: activeVariant?.option1 ?? null,
      option2: activeVariant?.option2 ?? null,
      option3: activeVariant?.option3 ?? null,
    };
    targetOptions[optionName] = optionValue;

    return this.variants.find(
      (variant) =>
        variant.option1 === targetOptions.option1 &&
        variant.option2 === targetOptions.option2 &&
        variant.option3 === targetOptions.option3
    );
  };

  cleanBundleOptionName = (name = '') =>
    name.replace(/\s*\((Color|Shade|Size)\)$/i, '').trim();

  getBundleOptionImageSrc = (image) => {
    if (!image) return '';
    if (typeof image === 'string') return image;
    return image.src || image.url || '';
  };

  findBundleComponentProduct = (optionValues = [], optionLabel = '') => {
    if (!window?.all_products) return null;

    const products = window.all_products;
    const cleanedLabel = this.cleanBundleOptionName(optionLabel);

    // Prefer matching by component product title from the option name
    if (cleanedLabel) {
      const titleMatchId = Object.keys(products).find((productId) => {
        const product = products[productId];
        if (!product || Number(productId) === Number(this.productJSON?.id)) return false;
        return product.title === cleanedLabel || product.title === optionLabel;
      });

      if (titleMatchId) {
        return { product: products[titleMatchId], productId: titleMatchId };
      }
    }

    // Fallback: match when every option value exists as a component variant title
    let matchedProduct = null;
    let matchedProductId = null;

    Object.keys(products).some((productId) => {
      const product = products[productId];
      if (!product || Number(productId) === Number(this.productJSON?.id)) return false;

      const variantTitles = new Set((product.variants || []).map((variant) => variant.title));
      const isExactMatch =
        optionValues.length > 0 && optionValues.every((value) => variantTitles.has(value));

      if (isExactMatch) {
        matchedProduct = product;
        matchedProductId = productId;
        return true;
      }
      return false;
    });

    if (!matchedProduct) return null;
    return { product: matchedProduct, productId: matchedProductId };
  };

  buildBundleOptionMaps = () => {
    const maps = {};
    if (!this.form || !this.isBundle || !window?.all_products) return maps;

    const optionNames = (this.options || []).map((_, index) => `option${index + 1}`);
    optionNames.forEach((optionName) => {
      const optionContainer = this.form.querySelector(
        `.wc-product-options-container[data-option-key="${optionName}"]`
      );
      if (!optionContainer) return;

      const optionInputs = Array.from(
        this.form.querySelectorAll(`input[name="${optionName}"][data-target="options"]`)
      );
      if (!optionInputs.length) return;

      const optionValues = [...new Set(optionInputs.map((input) => input.value))];
      const optionLabel = optionContainer.dataset.option || '';
      const matched = this.findBundleComponentProduct(optionValues, optionLabel);
      if (!matched) return;

      const { product, productId } = matched;
      const colorMap = {};
      const imageMap = {};
      const unitMap = {};
      let fallbackImage = this.getBundleOptionImageSrc(product.featured_image);

      (product.variants || []).forEach((variant) => {
        if (!optionValues.includes(variant.title)) return;

        const variantColor =
          window.metafields?.products?.[productId]?.color?.[variant.id]?.[0];
        if (variantColor) {
          colorMap[variant.title] = variantColor;
        }

        const variantImage = this.getBundleOptionImageSrc(variant.featured_image);
        if (variantImage) {
          imageMap[variant.title] = variantImage;
          if (!fallbackImage) fallbackImage = variantImage;
        }

        const unitMeasure =
          window.metafields?.products?.[productId]?.variants?.[variant.id]?.unit_measure;
        if (unitMeasure) {
          unitMap[variant.title] = unitMeasure;
        }
      });

      const subtitle =
        window.metafields?.products?.[productId]?.product_subtitle ||
        window.metafields?.products?.[productId]?.product_subtitle?.value ||
        '';

      maps[optionName] = {
        productId,
        title: product.title || this.cleanBundleOptionName(optionLabel),
        subtitle: typeof subtitle === 'string' ? subtitle : '',
        colors: colorMap,
        images: imageMap,
        units: unitMap,
        fallbackImage,
        isColorOption: /color|shade/i.test(optionLabel),
      };
    });

    return maps;
  };

  buildBundleOptionColorMaps = () => {
    const maps = {};
    Object.keys(this.bundleOptionMaps || {}).forEach((optionName) => {
      const colors = this.bundleOptionMaps[optionName]?.colors;
      if (colors && Object.keys(colors).length) {
        maps[optionName] = colors;
      }
    });

    if (Object.keys(maps).length) {
      return maps;
    }

    // Fallback for multi-option products that still need swatch colors
    if (!this.form || !window?.all_products || !window?.metafields?.products) {
      return maps;
    }

    const optionNames = (this.options || []).map((_, index) => `option${index + 1}`);
    optionNames.forEach((optionName) => {
      const optionInputs = Array.from(
        this.form.querySelectorAll(`input[name="${optionName}"][data-target="options"]`)
      );
      if (!optionInputs.length) return;

      const optionValues = [...new Set(optionInputs.map((input) => input.value))];
      if (!optionValues.length) return;

      const matched = this.findBundleComponentProduct(optionValues);
      if (!matched) return;

      const { product, productId } = matched;
      const colorMap = {};
      (product?.variants || []).forEach((variant) => {
        if (!optionValues.includes(variant.title)) return;
        const variantColor = window.metafields?.products?.[productId]?.color?.[variant.id]?.[0];
        if (variantColor) {
          colorMap[variant.title] = variantColor;
        }
      });

      if (Object.keys(colorMap).length) {
        maps[optionName] = colorMap;
      }
    });

    return maps;
  };

  parseShopifyGid = (gid = '') => String(gid).split('/').pop() || '';

  // Cheap local test so ordinary PDPs never pay for the components request. A bundle names its
  // options after the component products, so an option that resolves to a real product title is
  // the tell - and it still holds when only one component is selectable.
  looksLikeBundle = () => {
    if (this.isBundle) return true;

    // A bundle whose components are ALL locked to one variant gets no options at all - Shopify
    // gives it the placeholder "Title" / "Default Title". There is no option name to recognise it
    // by, so the title test below can never fire and the components request was never made: the
    // one bundle shape that depends entirely on that request was the one shape excluded from it.
    //
    // This also lets ordinary optionless products through, at the cost of a single small GraphQL
    // query that returns an empty array. That is the price of covering the case at all, since
    // nothing in the Liquid product object distinguishes a bundle from a plain product.
    const options = this.options || [];
    const hasOnlyDefaultOption =
      options.length <= 1 && (!options[0] || options[0] === 'Title');
    if (hasOnlyDefaultOption) return true;

    if (!window?.all_products || !options.length) return false;

    const productTitles = new Set(
      Object.keys(window.all_products)
        .filter((productId) => Number(productId) !== Number(this.productJSON?.id))
        .map((productId) => window.all_products[productId]?.title)
    );

    return this.options.some((optionName) =>
      productTitles.has(this.cleanBundleOptionName(optionName))
    );
  };

  fetchBundleComponents = async (variantId) => {
    if (!variantId) return [];

    const storefrontAccessToken =
      this.lowInventoryConfig?.storefrontAccessToken || STOREFRONT_ACCESS_TOKEN;
    if (!storefrontAccessToken) return [];

    const response = await fetch(`/api/${STOREFRONT_API_VERSION}/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': storefrontAccessToken,
      },
      body: JSON.stringify({
        query: BUNDLE_COMPONENTS_QUERY,
        variables: { variantId: `gid://shopify/ProductVariant/${variantId}` },
      }),
    });

    if (!response.ok) {
      throw new Error(`Bundle components request failed with status ${response.status}`);
    }

    const payload = await response.json();
    if (payload.errors?.length) {
      throw new Error(payload.errors.map((error) => error.message).join(', '));
    }

    return payload.data?.node?.components?.nodes || [];
  };

  // Titles already on the page: one per option, plus any card the bundle_fixed_components
  // metafield rendered server-side. Anything left over is a component Liquid couldn't see.
  getRenderedComponentTitles = () => {
    const optionTitles = (this.options || []).map((optionName) =>
      this.cleanBundleOptionName(optionName)
    );
    const serverRendered = Array.from(
      this.form?.querySelectorAll('[data-fixed-component-title]') || []
    ).map((node) => node.dataset.fixedComponentTitle);

    return new Set([...optionTitles, ...serverRendered]);
  };

  buildFixedComponentCard = (componentVariant) => {
    if (!componentVariant?.product) return null;

    const productId = this.parseShopifyGid(componentVariant.product.id);
    const variantId = this.parseShopifyGid(componentVariant.id);
    const productMeta = window.metafields?.products?.[productId];

    const title = componentVariant.product.title || '';
    const swatchColor = productMeta?.color?.[variantId]?.[0] || '';
    const unitMeasure = productMeta?.variants?.[variantId]?.unit_measure || '';
    const subtitle =
      typeof productMeta?.product_subtitle === 'string' ? productMeta.product_subtitle : '';
    const image =
      componentVariant.image?.url || componentVariant.product.featuredImage?.url || '';
    const isColorOption = Boolean(swatchColor);

    const container = document.createElement('div');
    container.className =
      'wc-product-options-container wc-product-options-container--bundle wc-product-options-container--fixed';
    container.dataset.fixedComponentTitle = title;

    const labelClass = isColorOption
      ? 'product-options__option-label-color'
      : 'product-options__option-label-size';

    // A component product with a single variant has no real option value - Shopify calls it
    // "Default Title". Rendering it gives the card a pill reading "Default Title" and a line
    // underneath repeating it, which tells the shopper nothing. The image, title and subtitle
    // already identify the component, so drop the option row entirely for these.
    // The radio on these cards is disabled, so FormData never sees it and getActiveVariant() does
    // not depend on it - unlike the Liquid-rendered rows, this one is safe to drop outright.
    const hasRealOptionValue =
      !this.disableVariantButton &&
      Boolean(componentVariant.title) &&
      componentVariant.title !== 'Default Title';

    container.innerHTML = `
      <div class="wc-product-bundle-option wc-product-bundle-option--fixed">
        <div class="wc-product-bundle-option__image">
          <img
            src="${this.escapeHtml(image)}"
            alt="${this.escapeHtml(title)}"
            width="96"
            height="96"
            loading="lazy"
          >
        </div>
        <div class="wc-product-bundle-option__content">
          <p class="product-options__name">${this.escapeHtml(title)}</p>
          ${
            subtitle
              ? `<p class="wc-product-bundle-option__subtitle">${this.escapeHtml(subtitle)}</p>`
              : ''
          }
          ${
            hasRealOptionValue
              ? `<div class="${isColorOption ? 'product-options__color' : ''}">
            <div class="product-options__options ${
              isColorOption ? 'product-options__options--color' : 'product-options__options--size'
            }">
              <span class="product-options__option-wrapper product-options__option-wrapper--fixed">
                <input
                  type="radio"
                  class="product-options__option"
                  value="${this.escapeHtml(componentVariant.title)}"
                  tabindex="-1"
                  aria-hidden="true"
                  checked
                  disabled
                >
                <div class="product-options__option-label ${labelClass}"${
                  isColorOption ? ` style="background-color: ${this.escapeHtml(swatchColor)};"` : ''
                }>
                  ${
                    isColorOption
                      ? `<span class="visually-hidden">${this.escapeHtml(componentVariant.title)}</span>`
                      : `<span class="size-measure">${this.escapeHtml(
                          componentVariant.title
                        )}<small>${this.escapeHtml(unitMeasure)}</small></span>`
                  }
                </div>
              </span>
            </div>
            <div class="wc-product-bundle-option__fixed-value">
              ${this.formatOptionSelectedLabel(componentVariant.title, unitMeasure)}
            </div>
          </div>`
              : ''
          }
        </div>
      </div>
    `;

    return container;
  };

  // window.all_products is built in theme.liquid from collections['all-products'], which does not
  // contain bundle component products - on The Mini Look on Lock Duo it holds 57 products and
  // neither "Mini Primer" nor "Setting Spray" is among them. findBundleComponentProduct therefore
  // resolves nothing, buildBundleOptionMaps returns no entry for the option, and every card falls
  // back to the bundle's own featured image, identically, for every selection.
  //
  // The components query already carries each component variant's image and was throwing it away
  // for any component that had a rendered option card. This folds it into the same map
  // updateBundleOptionImages reads, keyed by option value, so no other code has to change.
  //
  // A request only ever returns the components of ONE bundle variant, so the map fills in as the
  // shopper moves between them rather than all at once.
  cacheBundleComponentImages = (components = []) => {
    if (!this.form) return;

    const containers = Array.from(
      this.form.querySelectorAll('.wc-product-options-container[data-option-key]')
    );

    components.forEach((component) => {
      const variant = component?.productVariant;
      const productTitle = variant?.product?.title;
      const image = variant?.image?.url || variant?.product?.featuredImage?.url;
      if (!productTitle || !image || !variant.title) return;

      // The option is named "<Component product> (Size)" and its values are that component's
      // variant titles, so the product title identifies the card and the variant title the value.
      const container = containers.find(
        (el) => this.cleanBundleOptionName(el.dataset.option || '') === productTitle
      );
      const optionName = container?.dataset.optionKey;
      if (!optionName) return;

      this.bundleOptionMaps = this.bundleOptionMaps || {};
      this.bundleOptionMaps[optionName] = this.bundleOptionMaps[optionName] || {};

      const optionData = this.bundleOptionMaps[optionName];
      optionData.images = optionData.images || {};
      optionData.images[variant.title] = image;
      optionData.title = optionData.title || productTitle;
    });
  };

  // Components are per bundle variant, so each selection needs its own lookup. Cached by variant
  // id because a shopper toggling back and forth would otherwise refetch on every click.
  refreshBundleComponentImages = async (variantId) => {
    if (!variantId || !this.isBundle) return;

    this.fetchedComponentVariants = this.fetchedComponentVariants || new Set();
    if (this.fetchedComponentVariants.has(variantId)) return;
    this.fetchedComponentVariants.add(variantId);

    try {
      const components = await this.fetchBundleComponents(variantId);
      if (!components.length) return;
      this.cacheBundleComponentImages(components);
      this.updateBundleOptionImages();
    } catch (error) {
      // The cards keep the fallback image. Never let this break variant selection.
      console.error('Unable to refresh bundle component images:', error);
    }
  };

  initFixedBundleComponents = async () => {
    if (!this.form || !this.looksLikeBundle()) return;

    const variantId = this.getActiveVariant()?.id || this.variants?.[0]?.id;
    if (!variantId) return;

    let components = [];
    try {
      this.fetchedComponentVariants = this.fetchedComponentVariants || new Set();
      this.fetchedComponentVariants.add(variantId);
      components = await this.fetchBundleComponents(variantId);
    } catch (error) {
      console.error('Unable to load bundle components:', error);
      return;
    }

    if (!components.length) return;

    // Components only ever resolve on a real bundle, so set the flag here rather than after the
    // bail below - updateBundleOptionImages and refreshBundleComponentImages both no-op while
    // isBundle is false, which would strand the one shape that has nothing to inject.
    this.isBundle = true;
    this.classList.add('wc-product-form--bundle');

    // Before the missing-components bail below: a bundle whose components all have option cards
    // returns early, and that is exactly the shape whose images were never being set.
    this.cacheBundleComponentImages(components);
    this.updateBundleOptionImages();

    const rendered = this.getRenderedComponentTitles();
    const missing = components.filter(
      (component) => !rendered.has(component?.productVariant?.product?.title)
    );
    if (!missing.length) return;

    const fragment = document.createDocumentFragment();
    missing.forEach((component) => {
      const card = this.buildFixedComponentCard(component.productVariant);
      if (card) fragment.appendChild(card);
    });

    if (!fragment.childNodes.length) return;

    if (this.variantInput) {
      this.variantInput.before(fragment);
    } else {
      this.form.appendChild(fragment);
    }
  };

  initBundleOptions = () => {
    if (!this.isBundle || !this.form) return;

    Object.keys(this.bundleOptionMaps || {}).forEach((optionName) => {
      const optionData = this.bundleOptionMaps[optionName];
      const optionContainer = this.form.querySelector(
        `.wc-product-options-container[data-option-key="${optionName}"]`
      );
      if (!optionContainer || !optionData) return;

      const titleEl = optionContainer.querySelector('[data-bundle-option-title]');
      const subtitleEl = optionContainer.querySelector('[data-bundle-option-subtitle]');
      const imageEl = optionContainer.querySelector('[data-bundle-option-image]');

      if (titleEl && optionData.title) {
        titleEl.textContent = optionData.title;
      }

      if (subtitleEl) {
        if (optionData.subtitle) {
          subtitleEl.textContent = optionData.subtitle;
          subtitleEl.hidden = false;
        } else {
          subtitleEl.hidden = true;
        }
      }

      // Skip when the option has per-value swatch images: those are specific to what is selected,
      // and the component product's featured image is a generic stand-in for the whole option.
      // updateBundleOptionImages right below picks the correct one either way - this only avoids a
      // visible flash of the wrong image between the two.
      const hasValueImages = Boolean(
        Object.keys(this.optionValueImages?.[optionName] || {}).length
      );

      if (imageEl && optionData.fallbackImage && !hasValueImages) {
        imageEl.src = optionData.fallbackImage;
        imageEl.alt = optionData.title || '';
      }
    });

    this.updateBundleOptionImages(this.getActiveVariant());
    this.updateOptionSelectedLabels(this.getActiveVariant());
  };

  escapeHtml = (value = '') =>
    String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  formatOptionSelectedLabel = (optionValue, unitMeasure) => {
    if (!optionValue) return '';

    const value = String(optionValue);
    if (unitMeasure) {
      const unit = String(unitMeasure).replace(/^\(|\)$/g, '').trim();
      return `<strong>${this.escapeHtml(value)}</strong> - (${this.escapeHtml(unit)})`;
    }

    const separatorIndex = value.indexOf(' - ');
    if (separatorIndex > -1) {
      const lead = value.slice(0, separatorIndex);
      const rest = value.slice(separatorIndex + 3);
      return `<strong>${this.escapeHtml(lead)}</strong> - ${this.escapeHtml(rest)}`;
    }

    return `<strong>${this.escapeHtml(value)}</strong>`;
  };

  updateOptionSelectedLabels = (activeVariant = this.getActiveVariant()) => {
    this.optionSelectedList?.forEach((el) => {
      const optionIndex = Number(el.dataset.optionIndex || 0);
      const optionName = optionIndex ? `option${optionIndex}` : null;
      const optionValue = optionName ? activeVariant?.[optionName] : null;
      const unitMeasure = optionName
        ? this.bundleOptionMaps?.[optionName]?.units?.[optionValue]
        : null;

      el.innerHTML = this.formatOptionSelectedLabel(
        optionValue || activeVariant?.title,
        unitMeasure
      );
    });
  };

  // Swatch images keyed optionName -> value -> url, from product_option_value.swatch.image.
  buildOptionValueImages = () => {
    const maps = {};
    (this.productJSON?.option_value_images || []).forEach((entry) => {
      if (!entry?.option || !entry?.value || !entry?.image) return;
      maps[entry.option] = maps[entry.option] || {};
      maps[entry.option][entry.value] = entry.image;
    });

    return maps;
  };

  updateBundleOptionImages = (activeVariant = this.getActiveVariant()) => {
    if (!this.isBundle || !this.form || !activeVariant) return;

    // Iterate the product's options, not bundleOptionMaps. An option whose component product
    // could not be resolved has no entry in that map, and skipping it left exactly the cards that
    // most need a thumbnail stuck on the bundle's own featured image.
    const optionNames = (this.options || []).map((_, index) => `option${index + 1}`);

    optionNames.forEach((optionName) => {
      const optionData = this.bundleOptionMaps?.[optionName];
      const optionContainer = this.form.querySelector(
        `.wc-product-options-container[data-option-key="${optionName}"]`
      );
      const imageEl = optionContainer?.querySelector('[data-bundle-option-image]');
      if (!imageEl) return;

      const selectedValue = activeVariant[optionName];

      // The option value's own swatch image wins. It is the only source that identifies the card
      // on a bundle whose option is a category and whose values are the products to choose from -
      // there the option name matches no product and the components need not be on the storefront.
      //
      // The component-variant image below it is no longer gated on isColorOption: the gate meant a
      // "Primer" or "Size" option silently kept the fallback even when a per-value image existed.
      const nextImage =
        this.optionValueImages?.[optionName]?.[selectedValue] ||
        optionData?.images?.[selectedValue] ||
        optionData?.fallbackImage ||
        imageEl.src;

      if (nextImage && imageEl.src !== nextImage) {
        imageEl.src = nextImage;
      }
    });
  };

  updateSwatchInputs = (inputs, activeVariant) => {
    inputs?.forEach((input) => {
      const wrapper = input.closest('.product-options__option-wrapper');
      const swatch = wrapper?.querySelector('.product-options__option-label-color');
      if (!wrapper || !swatch) return;

      const optionName = input.getAttribute('name');
      const optionValue = input.value;
      const matchingVariant = this.getVariantForOptionValue(
        activeVariant,
        optionName,
        optionValue
      );
      const optionColorMap = this.bundleOptionColorMaps?.[optionName];
      const mappedColor = optionColorMap?.[optionValue];
      const color = mappedColor || this.backgrounds?.[matchingVariant?.id];

      const matchingVariantData = this.variantsData?.find(
        (data) => data.id === matchingVariant?.id
      );
      const isUnavailable = matchingVariant
        ? !matchingVariant.available || matchingVariantData?.is_soldout
        : false;

      if (color) {
        swatch.style.backgroundColor = color;
      }
      wrapper.classList.toggle('variant-unavailable', isUnavailable);
    });
  };

  updateStickySelectedSwatches = (activeVariant) => {
    if (!this.wcSticky) return;

    const isUnavailable = activeVariant?.available === false || activeVariant?.isOos;
    this.stickyOptionSelectedList?.forEach((el, index) => {
      const optionValue = activeVariant?.[`option${index + 1}`];
      el.textContent = optionValue || activeVariant?.title || '';
      el.classList.toggle('variant-unavailable', isUnavailable);
    });

    this.stickySelectedList?.forEach((el, index) => {
      const optionName = `option${index + 1}`;
      const selectedValue = activeVariant?.[optionName];
      const matchingVariant = this.getVariantForOptionValue(
        activeVariant,
        optionName,
        selectedValue
      );
      const mappedColor = this.bundleOptionColorMaps?.[optionName]?.[selectedValue];
      const color =
        mappedColor
        || this.backgrounds?.[matchingVariant?.id]
        || this.backgrounds?.[activeVariant?.id];

      if (color) {
        el.style.backgroundColor = color;
      }
      el.classList.toggle('variant-unavailable', isUnavailable);
    });
  };

  updateSwatchColors = (activeVariant = this.getActiveVariant()) => {
    const mainInputs = this.form?.querySelectorAll('[data-target="options"]');
    const stickyInputs = this.wcStickyForm?.querySelectorAll('[data-target="options"]');
    this.updateSwatchInputs(mainInputs, activeVariant);
    this.updateSwatchInputs(stickyInputs, activeVariant);
    this.updateStickySelectedSwatches(activeVariant);
  };

  updateVariantInput = (selector) => {
    if (this.variantChangeInProgress) return;
    this.variantChangeInProgress = true;
    // get the correct variant id, using getActiveVariant
    const selectorId = selector.getAttribute('id').replace('sticky-', '');
    document.querySelector(`#${selectorId}`).checked = true;
    const activeVariant = this.getActiveVariant();

    // set the value of the variant input to the variant id
    this.variantInput.value = activeVariant.id;

    if (this.wcSticky) {
      const stickyInput = document.querySelector(`#sticky-${selectorId}`);
      if (stickyInput) stickyInput.checked = true;
    }

    this.optionSelectedList?.forEach((el) => {
      const optionIndex = Number(el.dataset.optionIndex || 0);
      const optionName = optionIndex ? `option${optionIndex}` : null;
      const optionValue = optionName ? activeVariant?.[optionName] : null;
      const unitMeasure = optionName
        ? this.bundleOptionMaps?.[optionName]?.units?.[optionValue]
        : null;

      el.innerHTML = this.formatOptionSelectedLabel(
        optionValue || activeVariant.title,
        unitMeasure
      );
    });
    this.updateSwatchColors(activeVariant);
    this.updateBundleOptionImages(activeVariant);
    // Fire and forget - the new variant's component images are not in the map until its own
    // lookup returns, and updateBundleOptionImages keeps the current src until then.
    this.refreshBundleComponentImages(activeVariant?.id);

    // const matchingInputs = Array.from(
    //   document.querySelectorAll('input')
    // ).filter((input) => input.value === selector.value);
    // matchingInputs.forEach((input) => input.click());
    // trigger custom event that can be used to update the price or other elements
    document.dispatchEvent(
      new CustomEvent('variant:change', {
        bubbles: true,
        detail: { activeVariant, activeVariantIndex: activeVariant.index },
      })
    );
    this.variantChangeInProgress = false;
  };

  buyItNowHandle = (event) => {
    event.preventDefault();
    event.stopPropagation();

    const activeVariant = this.getActiveVariant();
    const canAddToCart = activeVariant.available && !activeVariant.isOos;

    if (!canAddToCart) {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth',
      });
      return;
    }

    const formData = new FormData(this.form);

    const originalProductQuantity = formData.get('quantity');

    const payload = {
      items: [
        {
          id: Number(activeVariant.id),
          quantity: originalProductQuantity,
        },
      ],
    };

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    })
      .then(async (response) => {
        const data = await response.json();
        window.trackEvents.addToCart({
          lineItems: data.items.map((item) => ({
            ...item,
            quantity: originalProductQuantity,
          })),
        });
        window.addInProgress = true;
        window.location.href = '/checkout';
        return false;
      })
      .catch((error) => {
        console.error('Error:', error);
      });
  };

  serializeForm = () => {
    const formData = new FormData(this.form);
    const serializedArray = [];

    for (const [name, value] of formData.entries()) {
      serializedArray.push({ name, value });
    }

    return serializedArray;
  };

  getActiveVariant = () => {
    const formData = this.serializeForm(this);
    const formOptions = {
      option1: null,
      option2: null,
      option3: null,
    };
    let selectedVariant = null;

    const formHasOptions = formData.some(
      (item) => item.name.indexOf('option') !== -1
    );

    if (formHasOptions) {
      formData.forEach((item) => {
        if (item.name.indexOf('option') !== -1) {
          formOptions[item.name] = item.value;
        }
      });

      this.variants.forEach((variant, index) => {
        if (
          variant.option1 === formOptions.option1 &&
          variant.option2 === formOptions.option2 &&
          variant.option3 === formOptions.option3
        ) {
          selectedVariant = variant;
          selectedVariant.index = index;
        }
      });
    } else {
      // eslint-disable-next-line prefer-destructuring
      selectedVariant = this.variants[0];
      selectedVariant.index = 0;
    }

    selectedVariant.isComing = this.variantsData.find(
      (data) => data.id === selectedVariant.id
    )?.is_coming;
    selectedVariant.isOos = this.variantsData.find(
      (data) => data.id === selectedVariant.id
    )?.is_soldout;

    return selectedVariant;
  };

  disconnectedCallback() {
    document.removeEventListener('DOMContentLoaded', this.trackViewItem);
    this.form?.removeEventListener('submit', this.handleAddToBag.bind(this));
    this.wcSticky.wcStickyForm.removeEventListener(
      'submit',
      this.handleAddToBag.bind(this)
    );
    // TODO: reinstate for price logic
    // this.form?.removeEventListener('change', this.validate);
    // this.form?.removeEventListener('load', this.validate);

    this.productOptionSelectors.forEach((selector) => {
      selector.removeEventListener('change', this.updateVariantInput());
    });
  }
}

if (!customElements.get('wc-product-form')) {
  customElements.define('wc-product-form', WCProductForm);
}
